import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { CalendarIcon, Plus, Trash2, Save, ArrowLeft, User } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Predefined options
const ORDERED_BY_OPTIONS = ["Rochelle", "Kristel", "Richly", "Ragnar", "Other"];
const UPSELLED_BY_OPTIONS = ["Wella", "Violet", "Mika", "Rose", "Pat", "NA", "Others"];
const CAUSE_CLASSIFICATION_OPTIONS = [
  "Cannot be Reached",
  "Rider Issue", 
  "Personal Problem",
  "Miscom with Ecom",
  "Miscom with Upseller",
  "JNT Preparation",
  "Buyer Declined"
];

// Platform options - with FIXED branch for each platform
// Shopee, TikTok, Lazada, FB/J&T-QC → QC only
// FB/J&T-PQ → PQ only
const PLATFORM_OPTIONS = [
  { value: "FB/J&T-QC", label: "FB/J&T - QC (Quezon City)", type: "facebook", fixedBranch: "QC" },
  { value: "FB/J&T-PQ", label: "FB/J&T - PQ (Parañaque)", type: "facebook", fixedBranch: "PQ" },
  { value: "Shopee", label: "Shopee", type: "marketplace", fixedBranch: "QC" },
  { value: "TikTok", label: "TikTok", type: "marketplace", fixedBranch: "QC" },
  { value: "Lazada", label: "Lazada", type: "marketplace", fixedBranch: "QC" },
];

// Branch options for restocking
const BRANCH_OPTIONS = [
  { value: "QC", label: "Quezon City (QC)" },
  { value: "PQ", label: "Parañaque (PQ)" },
];

// Preparer options - these are suggestions, user can type any name
const PREPARER_SUGGESTIONS = ["Wella", "Violet", "Mika", "Rose", "Pat", "Kyle", "Rochelle", "Kristel", "Richly", "Ragnar"];

// Normalize name to Title Case for consistent matching
const normalizeName = (name) => {
  if (!name) return "";
  return name.trim().toLowerCase().split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Check if platform is a Facebook/JNT order (needs upseller info)
const isFacebookOrder = (platform) => platform && (platform.startsWith("FB/J&T") || platform === "JNT");

const initialFormState = {
  platform: "", // FIRST: Platform where order came from
  tracking_number: "",
  date_ordered: null,
  customer_name: "",
  contact_number: "",
  address: "",
  ordered_by: "", // Order Creator (for Facebook/JNT)
  ordered_by_custom: "",
  upselled_by: "", // Upseller (for Facebook/JNT)
  upselled_by_custom: "",
  reason: "",
  cause_classification: "",
  preparers: [], // Who prepared the order - MULTIPLE preparers as array
  preparer: "", // Legacy field - will be joined from preparers array
  return_to_display: true, // Auto-add returned items to Display
  orders: [{ item_name: "", quantity: 1 }],
  notes: "",
  branch: "QC", // Branch for inventory return
};

export default function AddRTS() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [existingRecord, setExistingRecord] = useState(null);
  
  // Preparer autocomplete state
  const [preparerInput, setPreparerInput] = useState("");
  const [showPreparerSuggestions, setShowPreparerSuggestions] = useState(false);
  const [knownPreparers, setKnownPreparers] = useState(PREPARER_SUGGESTIONS);
  const preparerInputRef = useRef(null);
  
  // Item master list for autocomplete
  const [itemMasterList, setItemMasterList] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(null); // Which order item is being edited
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  
  // Fetch item master list
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/item-master/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // API returns { items: [...] }
        setItemMasterList(res.data?.items || res.data || []);
      } catch (err) {
        console.log("Could not fetch item master list");
        setItemMasterList([]);
      }
    };
    fetchItems();
  }, []);
  
  // Fetch existing preparers from RTS records
  useEffect(() => {
    const fetchPreparers = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/rts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingPreparers = res.data
          .map(r => r.preparer)
          .filter(p => p && p.trim())
          .map(p => normalizeName(p));
        
        // Combine with suggestions, remove duplicates
        const allPreparers = [...new Set([...PREPARER_SUGGESTIONS, ...existingPreparers])];
        setKnownPreparers(allPreparers.sort());
      } catch (err) {
        console.log("Could not fetch existing preparers");
      }
    };
    fetchPreparers();
  }, []);
  
  // Filter suggestions based on input
  const filteredPreparers = knownPreparers.filter(name => 
    name.toLowerCase().includes(preparerInput.toLowerCase()) &&
    !form.preparers.includes(name) // Don't show already added preparers
  );
  
  // Handle preparer input change
  const handlePreparerChange = (value) => {
    setPreparerInput(value);
    setShowPreparerSuggestions(value.length > 0);
  };
  
  // Add preparer to list
  const addPreparer = (name) => {
    const normalizedName = normalizeName(name);
    if (normalizedName && !form.preparers.includes(normalizedName)) {
      const newPreparers = [...form.preparers, normalizedName];
      setForm(prev => ({ 
        ...prev, 
        preparers: newPreparers,
        preparer: newPreparers.join(", ") // Keep legacy field updated
      }));
    }
    setPreparerInput("");
    setShowPreparerSuggestions(false);
  };
  
  // Remove preparer from list
  const removePreparer = (nameToRemove) => {
    const newPreparers = form.preparers.filter(name => name !== nameToRemove);
    setForm(prev => ({ 
      ...prev, 
      preparers: newPreparers,
      preparer: newPreparers.join(", ")
    }));
  };
  
  // Handle preparer selection from suggestions
  const selectPreparer = (name) => {
    addPreparer(name);
  };
  
  // Handle Enter key to add custom preparer
  const handlePreparerKeyDown = (e) => {
    if (e.key === 'Enter' && preparerInput.trim()) {
      e.preventDefault();
      addPreparer(preparerInput);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setForm((prev) => ({ ...prev, date_ordered: date }));
    setCalendarOpen(false);
  };

  const handleOrderChange = (index, field, value) => {
    const newOrders = [...form.orders];
    newOrders[index][field] = field === "quantity" ? parseInt(value) || 1 : value;
    setForm((prev) => ({ ...prev, orders: newOrders }));
  };

  const addOrderItem = () => {
    setForm((prev) => ({
      ...prev,
      orders: [...prev.orders, { item_name: "", quantity: 1 }],
    }));
  };

  const removeOrderItem = (index) => {
    if (form.orders.length > 1) {
      const newOrders = form.orders.filter((_, i) => i !== index);
      setForm((prev) => ({ ...prev, orders: newOrders }));
    }
  };

  const validateForm = () => {
    // Platform is required
    if (!form.platform) {
      toast.error("Please select a platform first");
      return false;
    }
    // Branch is required
    if (!form.branch) {
      toast.error("Piliin muna kung saang branch i-restock");
      return false;
    }
    if (!form.tracking_number.trim()) {
      toast.error("Tracking number is required");
      return false;
    }
    if (!form.date_ordered) {
      toast.error("Date ordered is required");
      return false;
    }
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required");
      return false;
    }
    // For Facebook/JNT orders, require upseller and order creator
    if (isFacebookOrder(form.platform)) {
      if (!form.upselled_by) {
        toast.error("Upseller is required for Facebook/JNT orders");
        return false;
      }
      if (form.upselled_by === "Others" && !form.upselled_by_custom.trim()) {
        toast.error("Please enter the upseller name");
        return false;
      }
      if (!form.ordered_by) {
        toast.error("Order Creator is required for Facebook/JNT orders");
        return false;
      }
      if (form.ordered_by === "Other" && !form.ordered_by_custom.trim()) {
        toast.error("Please enter the order creator name");
        return false;
      }
    }
    // Preparer is always required - at least one
    if (!form.preparers || form.preparers.length === 0) {
      toast.error("At least one preparer is required");
      return false;
    }
    if (!form.reason.trim()) {
      toast.error("Reason is required");
      return false;
    }
    if (!form.cause_classification) {
      toast.error("Cause classification is required");
      return false;
    }
    if (form.orders.some((order) => !order.item_name.trim())) {
      toast.error("All order items must have a name");
      return false;
    }
    return true;
  };

  // Check for duplicate tracking number (case-insensitive)
  const checkDuplicate = async () => {
    try {
      const normalizedTracking = form.tracking_number.trim().toLowerCase();
      const response = await axios.get(`${API}/rts?search=${form.tracking_number}`);
      
      // Find exact match (case-insensitive)
      const duplicate = response.data.find(
        record => record.tracking_number.toLowerCase() === normalizedTracking
      );
      
      return duplicate;
    } catch (error) {
      console.error("Error checking duplicate:", error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Check for duplicate
    const duplicate = await checkDuplicate();
    if (duplicate) {
      setExistingRecord(duplicate);
      setShowDuplicateDialog(true);
      return;
    }

    await createNewEntry();
  };

  const createNewEntry = async () => {
    setLoading(true);
    try {
      // Get final values (use custom if "Other" selected)
      const finalOrderedBy = form.ordered_by === "Other" ? form.ordered_by_custom : form.ordered_by;
      const finalUpselledBy = form.upselled_by === "Others" ? form.upselled_by_custom : form.upselled_by;

      const payload = {
        tracking_number: form.tracking_number.trim().toUpperCase(),
        date_ordered: format(form.date_ordered, "yyyy-MM-dd"),
        customer_name: form.customer_name.trim(),
        contact_number: form.contact_number.trim(),
        address: form.address.trim(),
        ordered_by: finalOrderedBy,
        upselled_by: finalUpselledBy || "",
        reason: form.reason.trim(),
        cause_classification: form.cause_classification,
        orders: form.orders,
        notes: form.notes.trim(),
        platform: form.platform || "",
        preparer: normalizeName(form.preparer) || "", // Normalize preparer name
        return_to_display: form.return_to_display !== false,
        branch: form.branch || "QC", // Branch where items will be returned to Display Area
      };

      await axios.post(`${API}/rts`, payload);
      toast.success("RTS entry created successfully!");
      setForm(initialFormState);
      setPreparerInput(""); // Clear preparer input
      navigate("/records");
    } catch (error) {
      console.error("Error creating RTS:", error);
      toast.error(error.response?.data?.detail || "Failed to create RTS entry");
    } finally {
      setLoading(false);
    }
  };

  const updateExistingEntry = async () => {
    if (!existingRecord) return;
    
    setLoading(true);
    try {
      const finalOrderedBy = form.ordered_by === "Other" ? form.ordered_by_custom : form.ordered_by;
      const finalUpselledBy = form.upselled_by === "Others" ? form.upselled_by_custom : form.upselled_by;

      const payload = {
        tracking_number: form.tracking_number.trim().toUpperCase(),
        date_ordered: format(form.date_ordered, "yyyy-MM-dd"),
        customer_name: form.customer_name.trim(),
        contact_number: form.contact_number.trim(),
        address: form.address.trim(),
        ordered_by: finalOrderedBy,
        upselled_by: finalUpselledBy || "",
        reason: form.reason.trim(),
        cause_classification: form.cause_classification,
        orders: form.orders,
        notes: form.notes.trim(),
        platform: form.platform || "",
        preparer: normalizeName(form.preparer) || "", // Normalize preparer name
      };

      await axios.put(`${API}/rts/${existingRecord.id}`, payload);
      toast.success("RTS entry updated successfully!");
      setShowDuplicateDialog(false);
      setForm(initialFormState);
      setPreparerInput(""); // Clear preparer input
      navigate("/records");
    } catch (error) {
      console.error("Error updating RTS:", error);
      toast.error("Failed to update RTS entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="add-rts-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New RTS</h1>
          <p className="text-gray-500 mt-1">I-encode ang bagong Return To Sender entry</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate("/records")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Records
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Platform & Tracking Information */}
          <Card className="form-section">
            <CardHeader>
              <CardTitle className="text-lg">Order Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* PLATFORM FIRST */}
              <div>
                <Label className="text-indigo-600 font-semibold">Platform *</Label>
                <Select 
                  value={form.platform} 
                  onValueChange={(value) => {
                    // Find the platform option to get the fixed branch
                    const platformOption = PLATFORM_OPTIONS.find(p => p.value === value);
                    const fixedBranch = platformOption?.fixedBranch || "QC";
                    
                    setForm(prev => ({
                      ...prev, 
                      platform: value,
                      branch: fixedBranch, // AUTO-SET BRANCH based on platform
                      // Clear upseller fields if switching to marketplace
                      upselled_by: isFacebookOrder(value) ? prev.upselled_by : "",
                      upselled_by_custom: isFacebookOrder(value) ? prev.upselled_by_custom : ""
                    }));
                  }}
                >
                  <SelectTrigger data-testid="select-platform" className="border-indigo-200 focus:ring-indigo-500">
                    <SelectValue placeholder="Select platform first" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORM_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label} → {option.fixedBranch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {form.platform === "JNT" ? "Facebook order via J&T - need upseller/order creator info" : 
                   form.platform ? `Returns will go to ${PLATFORM_OPTIONS.find(p => p.value === form.platform)?.fixedBranch || "QC"} Display Area` : ""}
                </p>
              </div>

              <div>
                <Label htmlFor="tracking_number">Tracking Number *</Label>
                <Input
                  id="tracking_number"
                  name="tracking_number"
                  placeholder={form.platform ? `Enter ${form.platform} tracking number` : "Select platform first"}
                  value={form.tracking_number}
                  onChange={handleInputChange}
                  className="font-mono uppercase"
                  data-testid="input-tracking-number"
                />
              </div>

              <div>
                <Label>Date Ordered *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !form.date_ordered && "text-muted-foreground"
                      )}
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.date_ordered ? (
                        format(form.date_ordered, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date_ordered}
                      onSelect={handleDateSelect}
                      initialFocus
                      data-testid="date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card className="form-section">
            <CardHeader>
              <CardTitle className="text-lg">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  placeholder="Enter customer name"
                  value={form.customer_name}
                  onChange={handleInputChange}
                  data-testid="input-customer-name"
                />
              </div>

              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  name="contact_number"
                  placeholder="Enter contact number"
                  value={form.contact_number}
                  onChange={handleInputChange}
                  data-testid="input-contact-number"
                />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="Enter complete address"
                  value={form.address}
                  onChange={handleInputChange}
                  rows={3}
                  data-testid="input-address"
                />
              </div>
            </CardContent>
          </Card>

          {/* Staff Information */}
          <Card className="form-section">
            <CardHeader>
              <CardTitle className="text-lg">Staff Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show Upseller and Order Creator ONLY for JNT/Facebook orders */}
              {isFacebookOrder(form.platform) && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700 font-medium">
                      📱 Facebook Order via J&T - Please provide upseller and order creator
                    </p>
                  </div>
                  
                  <div>
                    <Label>Upseller *</Label>
                    <Select 
                      value={form.upselled_by} 
                      onValueChange={(value) => setForm({...form, upselled_by: value, upselled_by_custom: ""})}
                    >
                      <SelectTrigger data-testid="select-upselled-by">
                        <SelectValue placeholder="Who did the upselling?" />
                      </SelectTrigger>
                      <SelectContent>
                        {UPSELLED_BY_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.upselled_by === "Others" && (
                      <Input
                        className="mt-2"
                        placeholder="Enter upseller name"
                        value={form.upselled_by_custom}
                        onChange={(e) => setForm({...form, upselled_by_custom: e.target.value})}
                        data-testid="input-upselled-by-custom"
                      />
                    )}
                  </div>
                  
                  <div>
                    <Label>Order Creator *</Label>
                    <Select 
                      value={form.ordered_by} 
                      onValueChange={(value) => setForm({...form, ordered_by: value, ordered_by_custom: ""})}
                    >
                      <SelectTrigger data-testid="select-ordered-by">
                        <SelectValue placeholder="Who created the order?" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDERED_BY_OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.ordered_by === "Other" && (
                      <Input
                        className="mt-2"
                        placeholder="Enter staff name"
                        value={form.ordered_by_custom}
                        onChange={(e) => setForm({...form, ordered_by_custom: e.target.value})}
                        data-testid="input-ordered-by-custom"
                      />
                    )}
                  </div>
                </>
              )}

              {/* For marketplace orders, show simplified info */}
              {form.platform && !isFacebookOrder(form.platform) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700 font-medium">
                    🛒 {form.platform} Marketplace - Customer ordered directly
                  </p>
                </div>
              )}

              <div className="relative">
                <Label className="text-green-600 font-semibold flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Preparer(s) *
                </Label>
                
                {/* Tags for added preparers */}
                {form.preparers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2 mt-1">
                    {form.preparers.map((name, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        <User className="w-3 h-3" />
                        {name}
                        <button
                          type="button"
                          onClick={() => removePreparer(name)}
                          className="hover:bg-green-200 rounded-full p-0.5"
                        >
                          <Trash2 className="w-3 h-3 text-green-600 hover:text-red-500" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="relative">
                  <Input
                    ref={preparerInputRef}
                    value={preparerInput}
                    onChange={(e) => handlePreparerChange(e.target.value)}
                    onFocus={() => setShowPreparerSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowPreparerSuggestions(false), 200)}
                    onKeyDown={handlePreparerKeyDown}
                    placeholder={form.preparers.length > 0 ? "Add another preparer..." : "Type preparer name (e.g., Kyle, Wella)"}
                    className="border-green-200 focus:ring-green-500"
                    data-testid="input-preparer"
                  />
                  
                  {/* Add button */}
                  {preparerInput.trim() && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => addPreparer(preparerInput)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <Plus className="w-4 h-4" />
                      Add
                    </Button>
                  )}
                  
                  {/* Autocomplete suggestions dropdown */}
                  {showPreparerSuggestions && filteredPreparers.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                      {filteredPreparers.map((name, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 focus:bg-green-50 focus:outline-none flex items-center gap-2"
                          onMouseDown={() => selectPreparer(name)}
                        >
                          <User className="w-3 h-3 text-gray-400" />
                          <span>{name}</span>
                          {preparerInput && name.toLowerCase().startsWith(preparerInput.toLowerCase()) && (
                            <span className="ml-auto text-xs text-green-600">Match</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Type a name and press Enter or click Add. You can add multiple preparers.
                </p>
                {form.preparers.length > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ {form.preparers.length} preparer(s): <strong>{form.preparers.join(", ")}</strong>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reason & Classification */}
          <Card className="form-section">
            <CardHeader>
              <CardTitle className="text-lg">RTS Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="reason">Reason *</Label>
                <Input
                  id="reason"
                  name="reason"
                  placeholder="e.g., Wrong item, Damaged, Customer refused"
                  value={form.reason}
                  onChange={handleInputChange}
                  data-testid="input-reason"
                />
              </div>

              <div>
                <Label>Cause Classification *</Label>
                <Select 
                  value={form.cause_classification} 
                  onValueChange={(value) => setForm({...form, cause_classification: value})}
                >
                  <SelectTrigger data-testid="select-cause">
                    <SelectValue placeholder="Select cause classification" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAUSE_CLASSIFICATION_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Branch Selection for Restocking - AUTO-SET based on Platform, READ-ONLY */}
          <Card className="form-section lg:col-span-2 border-2 border-green-200 bg-green-50/30">
            <CardHeader>
              <CardTitle className="text-lg text-green-700">📍 Restock Branch (Auto-Set)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label className="text-green-700 font-semibold">Branch para sa returned items:</Label>
                {/* Show as read-only display, auto-set based on platform */}
                <div className="p-3 bg-green-100 border-2 border-green-300 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">
                      {form.branch === "QC" ? "🏢" : "🏬"}
                    </span>
                    <div>
                      <p className="font-bold text-green-800 text-lg">
                        {form.branch === "QC" ? "Quezon City (QC)" : "Parañaque (PQ)"}
                      </p>
                      <p className="text-xs text-green-600">
                        {form.platform ? `Auto-set dahil ${form.platform} platform` : "Piliin muna ang platform"}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  ⚠️ Branch ay FIXED base sa platform:<br/>
                  • Shopee, TikTok, Lazada, FB/J&T-QC → <strong>QC</strong><br/>
                  • FB/J&T-PQ → <strong>PQ</strong>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card className="form-section lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Order Items</CardTitle>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addOrderItem}
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="order-items-list">
                {form.orders.map((order, index) => {
                  // Filter items based on input
                  const filteredItems = itemMasterList.filter(item => 
                    item.item_name.toLowerCase().includes((order.item_name || "").toLowerCase())
                  ).slice(0, 10); // Limit to 10 suggestions
                  
                  return (
                    <div key={index} className="flex gap-4 items-end">
                      <div className="flex-1 relative">
                        <Label>Item Name *</Label>
                        <Input
                          placeholder="Type to search items..."
                          value={order.item_name}
                          onChange={(e) => {
                            handleOrderChange(index, "item_name", e.target.value);
                            setActiveItemIndex(index);
                            setShowItemSuggestions(true);
                          }}
                          onFocus={() => {
                            setActiveItemIndex(index);
                            setShowItemSuggestions(true);
                          }}
                          onBlur={() => setTimeout(() => setShowItemSuggestions(false), 200)}
                          data-testid={`input-item-name-${index}`}
                        />
                        
                        {/* Item suggestions dropdown */}
                        {showItemSuggestions && activeItemIndex === index && filteredItems.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                            {filteredItems.map((item, idx) => (
                              <button
                                key={idx}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none flex items-center justify-between"
                                onMouseDown={() => {
                                  handleOrderChange(index, "item_name", item.item_name);
                                  setShowItemSuggestions(false);
                                }}
                              >
                                <span className="font-medium">{item.item_name}</span>
                                {item.item_code && (
                                  <span className="text-xs text-gray-500">{item.item_code}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Show "no items found" message */}
                        {showItemSuggestions && activeItemIndex === index && order.item_name && filteredItems.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-yellow-50 border border-yellow-200 rounded-md shadow-lg p-2">
                            <p className="text-xs text-yellow-700">⚠️ Item not found in master list. Will be added as new.</p>
                          </div>
                        )}
                      </div>
                      <div className="w-24">
                        <Label>Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={order.quantity}
                          onChange={(e) =>
                            handleOrderChange(index, "quantity", e.target.value)
                          }
                          data-testid={`input-item-qty-${index}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeOrderItem(index)}
                        disabled={form.orders.length === 1}
                        data-testid={`remove-item-btn-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Type to search from {itemMasterList.length} items. Selected items will be added to Display Area of the chosen branch.
              </p>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="form-section lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Enter any additional notes or remarks..."
                value={form.notes}
                onChange={handleInputChange}
                rows={4}
                data-testid="input-notes"
              />
            </CardContent>
          </Card>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => setForm(initialFormState)}
            data-testid="reset-btn"
          >
            Reset Form
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
            data-testid="submit-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving..." : "Save RTS Entry"}
          </Button>
        </div>
      </form>

      {/* Duplicate Confirmation Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600">⚠️ Duplicate Entry Found</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              May existing RTS entry na may tracking number na <strong className="font-mono">{form.tracking_number}</strong>.
            </p>
            {existingRecord && (
              <div className="bg-gray-50 p-4 rounded-lg text-sm">
                <p><strong>Customer:</strong> {existingRecord.customer_name}</p>
                <p><strong>Date:</strong> {existingRecord.date_ordered}</p>
                <p><strong>Reason:</strong> {existingRecord.reason}</p>
              </div>
            )}
            <p className="text-gray-600">Ano ang gusto mong gawin?</p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowDuplicateDialog(false);
                createNewEntry();
              }}
            >
              Create New Anyway
            </Button>
            <Button 
              onClick={updateExistingEntry}
              className="bg-red-600 hover:bg-red-700"
            >
              Update Existing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
